import { useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@core/lib/supabase";
import { extractFunctionsErrorMessage } from "../lib/functionsError";
import { readableTextColor } from "@core/lib/templateContrast";
import { PollTemplateHeader } from "./PollTemplateHeader";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@core/components/ui/dialog";
import { Input } from "@core/components/ui/input";
import { Label } from "@core/components/ui/label";
import { Button } from "@core/components/ui/button";
import { Checkbox } from "@core/components/ui/checkbox";

interface Template {
  id: string;
  name: string;
  primary_color: string;
  secondary_color: string;
  logo_url: string | null;
  created_at: string;
}

interface TemplateEditorProps {
  template?: Template | null;
  onSave: () => void;
  onClose: () => void;
}

export function TemplateEditor({
  template,
  onSave,
  onClose,
}: TemplateEditorProps) {
  const { t } = useTranslation(["settings", "common"]);
  const [name, setName] = useState(template?.name || "");
  const [primaryColor, setPrimaryColor] = useState(
    template?.primary_color || "#2a5cdb",
  );
  const [secondaryColor, setSecondaryColor] = useState(
    template?.secondary_color || "#f0f0f0",
  );
  const [logoUrl, setLogoUrl] = useState(template?.logo_url || "");
  const [darkMode, setDarkMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    if (!name.trim()) {
      setError(t("settings:nameRequired"));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const body = {
        ...(template ? { id: template.id } : {}),
        name,
        primary_color: primaryColor,
        secondary_color: secondaryColor,
        logo_url: logoUrl || undefined,
      };

      const { error: invokeError } = await supabase.functions.invoke(
        "templates",
        { method: template ? "PATCH" : "POST", body },
      );

      if (invokeError) {
        setError(
          await extractFunctionsErrorMessage(
            invokeError,
            t("settings:errorSavingTemplate"),
          ),
        );
        return;
      }

      onSave();
    } catch (err) {
      setError(t("settings:errorSavingTemplate"));
    } finally {
      setSaving(false);
    }
  }

  const previewBg = darkMode ? "#1a1a19" : "#ffffff";
  const previewText = darkMode ? "#ffffff" : "#0b0b0b";

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {template ? t("settings:editTemplate") : t("settings:newTemplate")}
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-6 sm:grid-cols-2">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">{t("settings:templateName")}</Label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("settings:templateNamePlaceholder")}
                data-testid="template-editor-name"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">{t("settings:primaryColor")}</Label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="h-9 w-9 cursor-pointer rounded border border-border bg-transparent"
                />
                <Input
                  type="text"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="flex-1"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">{t("settings:secondaryColor")}</Label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  className="h-9 w-9 cursor-pointer rounded border border-border bg-transparent"
                />
                <Input
                  type="text"
                  value={secondaryColor}
                  onChange={(e) => setSecondaryColor(e.target.value)}
                  className="flex-1"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">{t("settings:logoUrl")}</Label>
              <Input
                type="url"
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                placeholder="https://example.com/logo.png"
              />
            </div>
          </div>

          <div>
            <p className="mb-2 text-xs font-medium text-muted-foreground">
              {t("settings:preview")}
            </p>

            <div
              className="mb-2 rounded-md border border-border p-3"
              style={{ background: previewBg }}
            >
              {logoUrl && (
                <div className="mb-2">
                  <img
                    src={logoUrl}
                    alt="Logo"
                    className="max-h-10 max-w-full"
                  />
                </div>
              )}

              <div
                className="mb-2 rounded p-2"
                style={{ background: primaryColor, color: "white" }}
              >
                <p className="text-xs font-medium">
                  {t("settings:samplePoll")}
                </p>
              </div>

              <div
                className="rounded p-2"
                style={{
                  background: secondaryColor,
                  border: `1px solid ${primaryColor}`,
                }}
              >
                <p className="text-xs" style={{ color: previewText }}>
                  Option 1
                </p>
                <p className="text-xs" style={{ color: previewText }}>
                  Option 2
                </p>
              </div>
            </div>

            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <Checkbox
                checked={darkMode}
                onCheckedChange={(checked) => setDarkMode(checked === true)}
              />
              {t("settings:darkModePreview")}
            </label>

            <p className="mb-2 mt-4 text-xs font-medium text-muted-foreground">
              {t("settings:preview")}
            </p>
            <div className="overflow-hidden rounded-md border border-border">
              <PollTemplateHeader
                template={{
                  id: "preview",
                  name,
                  primary_color: primaryColor,
                  secondary_color: secondaryColor,
                  logo_url: logoUrl || null,
                }}
                title={t("settings:samplePoll")}
              />
              <div className="p-3" style={{ background: previewBg }}>
                <button
                  type="button"
                  className="w-full rounded-lg py-2 text-xs font-bold"
                  style={{
                    backgroundColor: primaryColor,
                    color: readableTextColor(primaryColor),
                  }}
                  disabled
                >
                  {t("settings:samplePoll")}
                </button>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("common:cancel")}
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="template-editor-save"
          >
            {saving ? t("common:saving") : t("common:save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
